import { createContext, useContext, useEffect, useState } from 'react';
import type { Company, Domain, CustomerTargetGroup, Brand, DataContextType } from '../types/data';
import { supabase } from '../services/supabase';
import {
  fetchCompaniesData,
  fetchDomainsForCompany,
  fetchDomainRelatedData,
} from '../services/dataService';

const DataContext = createContext<DataContextType | undefined>(undefined);

export function DataProvider({ children }: { children: React.ReactNode }) {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [domains, setDomains] = useState<Domain[]>([]);
  const [targetCompany, setTargetCompany] = useState<string | null>(null);
  const [targetCompanyDetails, setTargetCompanyDetails] = useState<Company | null>(null);
  const [targetDomain, setTargetDomain] = useState<string | null>(null);
  const [customerTargetGroups, setCustomerTargetGroups] = useState<CustomerTargetGroup[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [companiesLoading, setCompaniesLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch domains for a specific company
  const fetchDomains = async (companyId?: string): Promise<void> => {
    const targetId = companyId || targetCompany;
    if (!targetId) return;

    setCustomerTargetGroups([]);
    setBrands([]);
    setDomains([]);
    setIsLoading(true);

    try {
      const domainsData = await fetchDomainsForCompany(targetId);
      setDomains(domainsData);
      setTargetDomain(domainsData.length > 0 ? domainsData[0].id : null);
    } catch (err) {
      console.error('Error fetching domains:', err);
      setError(err instanceof Error ? err.message : 'Error fetching domains');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch related domain data
  const fetchRelatedDomainData = async (domainId: string | null = targetDomain): Promise<void> => {
    if (!domainId) return;

    setCustomerTargetGroups([]);
    setBrands([]);
    setIsLoading(true);

    try {
      const { customerTargetGroups, brands } = await fetchDomainRelatedData(domainId);
      setCustomerTargetGroups(customerTargetGroups);
      setBrands(brands);
    } catch (err) {
      console.error('Error fetching related domain data:', err);
      setError(err instanceof Error ? err.message : 'Error fetching related domain data');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch companies
  const fetchCompanies = async () => {
    setIsLoading(true);
    setCompaniesLoading(true);

    try {
      setError(null);
      const companiesData = await fetchCompaniesData();
      setCompanies(companiesData);

      if (companiesData.length === 0) return;

      if (!targetCompany || !companiesData.some((company) => company.id === targetCompany)) {
        const firstCompany = companiesData[0];
        setTargetCompany(firstCompany.id);
        setTargetCompanyDetails(firstCompany);
        await fetchDomains(firstCompany.id);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'An error occurred while fetching data';
      console.error('Error fetching initial data:', errorMessage);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
      setCompaniesLoading(false);
    }
  };

  // Initial data fetch
  const fetchInitialData = async () => {
    await fetchCompanies();
  };

  // Watch for targetCompany changes
  useEffect(() => {
    if (targetCompany) {
      fetchDomains(targetCompany);
      setTargetCompanyDetails(companies.find((company) => company.id === targetCompany) || null);
    } else {
      setDomains([]);
      setTargetDomain(null);
    }
  }, [targetCompany]);

  // Watch for targetDomain changes
  useEffect(() => {
    if (targetDomain) {
      fetchRelatedDomainData(targetDomain);
    }
  }, [targetDomain]);

  // New method to trigger company fetch
  const triggerCompanyFetch = async () => {
    await fetchCompanies();
  };

  // New method to trigger domain fetch for a specific company ID
  const triggerDomainFetch = async (companyId: string) => {
    await fetchDomains(companyId);
  };

  const value: DataContextType = {
    companies,
    domains,
    customerTargetGroups,
    brands,
    isLoading,
    error,
    supabase,
    targetCompany,
    targetDomain,
    companiesLoading,
    targetCompanyDetails,
    setTargetCompany,
    setTargetDomain,
    refreshData: fetchInitialData,
    triggerCompanyFetch,
    triggerDomainFetch,
    fetchDomains,
    fetchRelatedDomainData,
    setIsLoading,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export function useData() {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
}
